import { Link } from "react-router-dom";
import styled, { css } from "styled-components/macro";

import { IEventStatus } from "Bands/types";
import { BoldText, TinyText } from "shared/styles";
import { borderRadius, colors, fontSize, space, transition, zIndex } from "shared/styles/constants";
import { Button } from "shared/components/Button/Button";

export const StyledCalendar = styled.div`
  overflow: hidden;

  display: grid;
  grid-gap: 1px;

  border-radius: ${borderRadius.m};
  background-color: ${colors.blackLight};
  border: 1px solid ${colors.blackLight};
`;

export const StyledMonth = styled.div`
  padding: ${space.s};

  background-color: ${colors.extraBlack};

  text-align: center;
`;

export const StyledWeekdays = styled.ul`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  grid-gap: 1px;

  background-color: ${colors.blackLight};

  text-align: center;
`;

export const StyledWeekday = styled.li`
  padding: ${space.xs} ${space.s};
  background-color: ${colors.blackLight};
`;

export const StyledDays = styled.ul`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  grid-gap: 1px;

  background-color: ${colors.blackLight};
`;

interface StyledDayProps {
  extraPadding?: boolean;
}

export const StyledDay = styled.li<StyledDayProps>`
  padding-bottom: ${({ extraPadding }) => (extraPadding ? space.xxl : space.s)};
  min-height: 120px;

  background-color: ${colors.extraBlack};

  position: relative;

  &:hover > .calendar-add-event-button {
    opacity: 1;
  }
`;

export interface DayNumberProps {
  isActive?: boolean;
  isDisabled?: boolean;
}

export const StyledDayNumber = styled(TinyText)<DayNumberProps>`
  float: right;
  margin: ${space.xs};

  display: flex;
  align-items: center;
  justify-content: center;

  width: 24px;
  height: 24px;

  background-color: ${colors.blackLight};
  border-radius: 50%;
  color: ${colors.grey4};

  ${({ isActive }) =>
    isActive &&
    css`
      background-color: ${colors.white};
      color: ${colors.black};
    `};

  ${({ isDisabled }) =>
    isDisabled &&
    css`
      background-color: transparent;
      color: ${colors.grey1};
    `};
`;

interface StyledCalendarItemProps {
  status?: IEventStatus;
}

export const StyledBoldText = styled(BoldText)`
  word-break: break-word;
  overflow-wrap: break-word;
`;

export const StyledCalendarItem = styled(Link)<StyledCalendarItemProps>`
  display: flex;
  width: 100%;

  border-left: 2px solid transparent;
  padding: 0 ${space.s};
  margin-top: ${space.xs};

  color: ${colors.grey5};
  ${fontSize.xs}
  text-decoration: none;

  transition: color ${transition.default};

  &[href] {
    &:hover,
    &:focus {
      color: ${colors.white};
    }
  }

  > * + * {
    margin-left: ${space.xs};
  }

  ${({ status }) => {
    switch (status) {
      case IEventStatus.confirmed:
        return css`
          border-left-color: ${colors.green};
        `;
      case IEventStatus.postponed:
      case IEventStatus.pending:
      case IEventStatus.onHold:
        return css`
          border-left-style: dotted;
          border-left-color: ${colors.yellow};
        `;
      case IEventStatus.canceled:
        return css`
          border-left-style: dotted;
          border-left-color: ${colors.red};

          color: ${colors.grey3};
          text-decoration: line-through;

          * {
            color: ${colors.grey3};
          }
        `;
    }
  }};
`;

export const StyledIcon = styled.span`
  flex: 0 0 auto;
  margin-top: 2px;
`;

export const StyledCalendarItemText = styled.span`
  display: flex;
  flex-direction: column;

  > * + * {
    margin-top: ${space.xxs};
  }
`;

export const AddEventButton = styled(Button)`
  position: absolute;
  z-index: ${zIndex.above};

  bottom: ${space.xs};
  left: 0;
  right: 0;

  margin: 0 auto;

  opacity: 0;
`;
